import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster } from "./@/components/ui/toaster";
import Dashboard from "./screens/Dashboard";
import AdminLogin from "./authScreens/AdminLogin";
import VendorDetails from "./screens/VendorDetails";
import Products from "./screens/ProductDetails";
import NotFound from "./components/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import OrdersForm from "./screens/OrderPage/OrdersForm";
import ProductFrom from "./components/ProductForm";
import Login from "./authScreens/Login";
import CustomerDetails from "./screens/CustomerDetails";
import ResetPassword from "./authScreens/ResetPassword";
import HomePage from "./components/DesktopHomepage/HomePage";
import MobileHomepage from "./components/MobileHomepage/MobileHomepage";
import PrivacyPolicy from "./screens/HomePage/PrivacyPolicy";
import CookieUse from "./screens/HomePage/CookieUse";
import About from "./screens/HomePage/About";
import { DEEP_LINK_BASE_URL, TABWIDTH, USER_ROLE } from "./lib/constants";
import StorePage from "./screens/StorePage";
import ReviewPage from "./screens/ReviewPage";
import ContactUs from "./screens/HomePage/ContactUs";
import TermsOfServiceContent from "./screens/HomePage/TermsOfUse";
import SignUpPage from "./authScreens/SignupPage";
import SignupSuccess from "./screens/SignupSuccess";
import { ForgotPassword } from "./authScreens/ForgotPassword";
import AdminConfigurationPage from "./screens/AdminConfigurationPage";
import OurActiveVendor from "./screens/HomePage/OurAvtiveVendor";
import Credentials from "./screens/HomePage/Credentials";
import Advantages from "./screens/HomePage/Advantages";
import ContactSupport from "./screens/HomePage/ContactSupport";
import ImprintDataProtection from "./screens/HomePage/ImprintDataProtection";
import Orders from "./screens/OrderPage";
import FAQPage from "./screens/HomePage/FAQs";
import AdminPayoutPage from "./screens/AdminPayoutPage";
import HomeLayout from "./screens/HomeLayout";
import DownPage from "./components/common/DownPage";
import PrintPdf from "./components/common/PrintPdf";
import GoogleCalenderRedirectPage from "./screens/GoogleCalenderRedirectPage";
import Bookings from "./screens/MeetingPage/Bookings";
import { IsServiceTypeVendor, useDecodedTokenResult } from "./lib/utils";
import VideoPage from "./screens/VideoPage";
import AddEditVideo from "./screens/VideoPage/AddEditVideo";
import ChatPage from "./screens/ChatPage";
import Loader from "./components/common/Loader";
import GoLivePage from "./screens/GoLivePage";
import { setupZoomHandler } from "./utils/zoomHandler";
import { setupViewportFix } from "./utils/viewportFix";
import LayoutSettingsPage from "./screens/LayoutSettingsPage";

const App: React.FC = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoading, setIsLoading] = useState(false);
  const vendorServiceType = IsServiceTypeVendor();
  const { role } = useDecodedTokenResult();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      if (newWidth !== windowWidth) {
        setWindowWidth(newWidth);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [windowWidth]);

  useEffect(() => {
    setupZoomHandler();
  }, []);

  useEffect(() => {
    setupViewportFix();
  }, []);

  const RedirectHtmlFiles = () => {
    const location = useLocation();

    useEffect(() => {
      const locationPath = location.pathname;
      const queryParams = location.search;

      const isIOSOrMac =
        /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent) &&
        typeof (window as any).MSStream === "undefined";

      const isAndroid = /Android/.test(navigator.userAgent);
      const isWindows = /Windows/.test(navigator.userAgent);

      if ((isIOSOrMac || isAndroid) && locationPath.startsWith("/Main")) {
        window.location.href = `deeplinkapp:/${locationPath}${queryParams}`;
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else if (isWindows && locationPath.startsWith("/Main")) {
        window.location.href = "/";
      } else if (
        !locationPath.endsWith(".html") &&
        ![
          "/services",
          "/add-service",
          "/products",
          "/add-product",
          "/payouts",
          "/configuration",
          "/customers",
          "/edit-product/:id",
          "/edit-service/:id",
          "/vendors",
        ].includes(locationPath)
      ) {
        window.location.href = `${locationPath}.html`;
      } else if (
        ["/payouts", "/configuration", "/customers", "/vendors"].includes(
          locationPath
        )
      ) {
        window.location.href = `/admin-login`;
      } else if (
        [
          "/services",
          "/add-service",
          "/products",
          "/add-product",
          "/edit-product/:id",
          "/edit-service/:id",
        ].includes(locationPath)
      ) {
        window.location.href = `/vendor-login`;
      }
    }, [location]);

    return null; // Render nothing since we are redirecting
  };

  return (
    <div className="app-container ios-viewport-fix">
      <Router>
        {!isOnline ? (
          <DownPage />
        ) : isLoading ? (
          <Loader />
        ) : (
          <Routes>
            {/* Auth Routes */}
            <Route path="/vendor-login" element={<Login />} />
            <Route path="/forgot-Password" element={<ForgotPassword />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/downpage" element={<DownPage />} />
            <Route path="/printPdf" element={<PrintPdf />} />
            {/* End of Auth Routes */}

            {/* Landing Page Routes */}
            <Route element={<HomeLayout />}>
              <Route
                index
                path="/"
                element={
                  windowWidth < TABWIDTH ? <MobileHomepage /> : <HomePage />
                }
              />
              <Route path="/terms-of-use" element={<TermsOfServiceContent />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-use" element={<CookieUse />} />
              <Route path="/contact-support" element={<ContactSupport />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/ourVendors" element={<OurActiveVendor />} />
              <Route path="/credentials" element={<Credentials />} />
              <Route path="/advantages" element={<Advantages />} />

              <Route
                path="/imprint-data-protection"
                element={<ImprintDataProtection />}
              />
              <Route path="/faqs" element={<FAQPage />} />
            </Route>
            {/* End of Landing Page Routes */}

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/orders" element={<Orders />} />
              <Route
                path="/add-video"
                element={
                  <AddEditVideo isForAddVideo={true} isForEditVideo={false} />
                }
              />
              <Route
                path="/edit-video/:id"
                element={
                  <AddEditVideo isForAddVideo={false} isForEditVideo={true} />
                }
              />
              <Route path="/videos" element={<VideoPage />} />
              {vendorServiceType ? (
                <>
                  <Route path="/services" element={<Products />} />
                  <Route
                    path="/add-service"
                    element={
                      <ProductFrom
                        isEditingProduct={false}
                        isAddingProduct={true}
                      />
                    }
                  />
                  <Route
                    path="/edit-service/:id"
                    element={
                      <ProductFrom
                        isEditingProduct={true}
                        isAddingProduct={false}
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <Route path="/products" element={<Products />} />
                  <Route
                    path="/add-product"
                    element={
                      <ProductFrom
                        isEditingProduct={false}
                        isAddingProduct={true}
                      />
                    }
                  />
                  <Route
                    path="/edit-product/:id"
                    element={
                      <ProductFrom
                        isEditingProduct={true}
                        isAddingProduct={false}
                      />
                    }
                  />
                </>
              )}
              <Route path="/edit-order/:id" element={<OrdersForm />} />
              {role === USER_ROLE.ADMIN && (
                <>
                  <Route path="/vendors" element={<VendorDetails />} />
                  <Route path="/customers" element={<CustomerDetails />} />
                  <Route
                    path="/configuration"
                    element={<AdminConfigurationPage />}
                  />
                  <Route path="/payouts" element={<AdminPayoutPage />} />
                </>
              )}
              <Route path="/store" element={<StorePage />} />
              <Route path="/reset-Password" element={<ResetPassword />} />
              <Route path="/reviews" element={<ReviewPage />} />
              <Route path="/chats" element={<ChatPage />} />
              <Route path="/goLive" element={<GoLivePage />} />
              <Route path="/layout" element={<LayoutSettingsPage />} />
              <Route
                path="/googleCalenderRedirectPage"
                element={<GoogleCalenderRedirectPage />}
              />
              <Route path="/bookings" element={<Bookings />} />
            </Route>
            {/* End of Protected Routes */}

            <Route path="*" element={<RedirectHtmlFiles />} />

            {/* Catch-all route for 404 Not Found */}
            <Route path="*" element={<NotFound />} />
            {/* End of Catch-all route */}
          </Routes>
        )}
      </Router>
      <Toaster />
    </div>
  );
};

export default App;
